import React, { useEffect, useState, useMemo, useRef } from "react";
import "./Dashboard.css";
import axios from "axios";
import moment from "moment";
import { models } from "powerbi-client";
import DashboardRefresh from "./DashboardRefresh";
import { PowerBIEmbed } from "powerbi-client-react";
import { useActivityLogger } from "../../hooks/useActivityLogger";

const LineListDashboard = ({ reportDetails }) => {
  const reportRef = useRef(null);
  const slicersUpdated = useRef(false);
  const slicersUpdatedCount = useRef(0);

  const { sessionUserBoValue } = useActivityLogger();
  const reportId = 4;

  const [reportInfo, setReportInfo] = useState(null);

  const fetchData = async () => {
    try {
      const response = await axios.post(
        process.env.NODE_ENV === "development"
          ? `http://localhost:9000/mrunner_api/getPowerBIToken`
          : `${window.location.protocol}//${window.location.host}/mrunner_api/getPowerBIToken`,
        { r_id: reportId }
      );
      // console.log("powerBI token", response);
      setReportInfo(response.data);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const userEmail = sessionUserBoValue?.email || null; //don't remove ? from here
  const userId = sessionUserBoValue?.userCredentials?.id || null; //don't remove ? from here

  // console.log("models", models);
  const embedConfig = useMemo(() => {
    const filters = [];

    if (userEmail) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: "public dim_userrole_details",
          column: "username",
        },
        operator: "In",
        values: [userEmail],
      });
    }
    if (userId) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: "public dim_userrole_details",
          column: "uid",
        },
        operator: "In",
        values: [userId],
      });
    }
    return {
      type: "report",
      id: reportInfo?.reportId,
      embedUrl: reportInfo?.embedUrl,
      tokenType: models.TokenType.Embed,
      accessToken: reportInfo?.finalToken,
      pageView: "actualSize",
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: false,
        layoutType: models.LayoutType.FitToWidth,
      },
      filters,
    };
  }, [reportInfo, userEmail, userId]);

  const refreshDashboard = () => {
    setReportInfo(null);
    fetchData();
  };

  const getAllFilters = async () => {
    if (!reportRef.current) return;

    try {
      const pages = await reportRef.current.getPages();

      for (const page of pages) {
        const visuals = await page.getVisuals();
        const slicers = visuals.filter((visual) => visual.type === "slicer");
        slicers.forEach(async (slicer) => {
          const slicerState = await slicer.getSlicerState();
          slicerState.filters.forEach((f) => {
            // console.log("f", f);
          });
        });
      }
    } catch (error) {
      console.error("Error getting filters:", error);
    }
  };

  const setSlicers = async () => {
    if (!reportRef.current) return;

    try {
      if (slicersUpdatedCount.current >= 10) {
        slicersUpdated.current = true;
      } else {
        slicersUpdatedCount.current += 1;
      }
      const pages = await reportRef.current.getPages();

      for (const page of pages) {
        const visuals = await page.getVisuals();
        const slicers = visuals.filter((visual) => visual.type === "slicer");

        for (const slicer of slicers) {
          const slicerState = await slicer.getSlicerState();

          // Determine which slicer we're working with
          const columnName = slicerState?.targets[0]?.column;
          const tableName = slicerState?.targets[0]?.table;

          // Set different values based on the slicer type
          if (
            columnName === "permanent_state" &&
            reportDetails.state !== "All"
          ) {
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#basicFilter",
                  target: {
                    table: tableName,
                    column: "permanent_state",
                  },
                  filterType: 1, // Basic filter
                  operator: "In",
                  values: [reportDetails.state], // Your default values here
                },
              ],
            });
          } else if (
            columnName === "Name of the Organization" &&
            reportDetails.facility !== "All"
          ) {
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#basicFilter",
                  target: {
                    table: tableName,
                    column: "Name of the Organization",
                  },
                  filterType: 1,
                  operator: "In",
                  values: [reportDetails.facility], // Your default values here
                },
              ],
            });
          } else if (columnName === "Date" && reportDetails.period !== "All") {
            const startOfMonthUTC = moment
              .utc(reportDetails.period, "MMM-YYYY")
              .startOf("month")
              .toISOString();
            // console.log("startOfMonthUTC", startOfMonthUTC);
            const endOfMonthUTC = moment
              .utc(reportDetails.period, "MMM-YYYY")
              .endOf("month")
              .toISOString();
            // console.log("endOfMonthUTC", endOfMonthUTC);
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#advanced",
                  target: {
                    table: "dim_date",
                    column: "Date",
                  },
                  logicalOperator: "And",
                  conditions: [
                    {
                      operator: "GreaterThanOrEqual",
                      value: startOfMonthUTC,
                    },
                    {
                      operator: "LessThan",
                      value: endOfMonthUTC,
                    },
                  ],
                  filterType: models.FilterType.Advanced,
                  requireSingleSelection: false,
                },
              ],
            });
          }
        }
      }
    } catch (error) {
      console.error("Error getting filters:", error);
    }
  };

  return (
    <div
      style={{
        padding: reportDetails ? "0px" : "65px 0px 0px",
        height: "calc(100vh - 64px)",
        width: "100%",
        position: "relative",
      }}
    >
      {reportInfo?.bearerToken && !reportDetails && (
        <DashboardRefresh
          reportId={reportId}
          accessToken={reportInfo?.bearerToken}
          groupId={reportInfo?.workspaceId}
          datasetId={reportInfo?.datasetId}
          refreshDashboard={refreshDashboard}
        />
      )}
      {reportInfo && (
        <PowerBIEmbed
          embedConfig={embedConfig}
          getEmbeddedComponent={(embeddedReport) => {
            reportRef.current = embeddedReport;
          }}
          eventHandlers={
            new Map([
              [
                "loaded",
                function () {
                  if (!reportDetails) {
                    // console.log("Report loaded");
                    getAllFilters();
                  }
                },
              ],
              [
                "rendered",
                function () {
                  if (!reportDetails) {
                    // console.log("Report rendered normal");
                    getAllFilters();
                  }
                  if (reportDetails && !slicersUpdated.current) {
                    // console.log("reportDetails", reportDetails);
                    // console.log("slicersUpdated", slicersUpdated.current);
                    // console.log("Report rendered from approval");
                    setSlicers();
                  }
                },
              ],
              [
                "filtersApplied",
                function () {
                  if (!reportDetails) {
                    // console.log("Filters changed by user normal");
                    getAllFilters();
                  }
                },
              ],
              [
                "error",
                function (event) {
                  console.log(event.detail);
                },
              ],
            ])
          }
          cssClassName="powerbi-container"
        />
      )}
    </div>
  );
};

export default LineListDashboard;
